import {
  memo,
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { VariableSizeList, type ListChildComponentProps } from 'react-window';

import type { TChatMessage } from '../model/types';

type ChildOptions = {
  chatMessage: TChatMessage;
  ref: RefObject<HTMLDivElement>;
};

type ChatMessagesListProps = {
  chatMessages: TChatMessage[];
  children: (options: ChildOptions) => ReactElement;
};

const DEFAULT_ROW_HEIGHT = 100;
const ITEMS_PER_VIEW = 3;
const MAX_CONTAINER_HEIGHT = 100 * ITEMS_PER_VIEW;

const ChatMessagesList = ({
  chatMessages,
  children,
}: ChatMessagesListProps) => {
  const listRef = useRef<VariableSizeList>(null);
  const rowHeightsCache = useRef<Record<number, number>>({});

  const setRowHeight = useCallback((index: number, size: number) => {
    rowHeightsCache.current = { ...rowHeightsCache.current, [index]: size };
    listRef.current?.resetAfterIndex(index);
  }, []);
  const getRowHeight = useCallback(
    (index: number) => rowHeightsCache.current[index] ?? DEFAULT_ROW_HEIGHT,
    []
  );

  const Row = memo(function Row({
    chatMessage,
    index,
  }: {
    chatMessage: TChatMessage;
    index: number;
  }) {
    const rowRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (rowRef.current) {
        setRowHeight(index, rowRef.current.getBoundingClientRect().height);
      }
    }, [index]);

    return children({ chatMessage, ref: rowRef });
  });

  const ListRow = useCallback(
    function ListRow({
      data,
      index,
      style,
    }: ListChildComponentProps<TChatMessage[]>) {
      const chatMessage = data[index];

      return (
        <div style={style}>
          <Row index={index} chatMessage={chatMessage} />
        </div>
      );
    },
    [Row]
  );

  return (
    <VariableSizeList
      height={MAX_CONTAINER_HEIGHT}
      itemCount={chatMessages.length}
      itemData={chatMessages}
      itemSize={getRowHeight}
      ref={listRef}
      width="100%"
    >
      {ListRow}
    </VariableSizeList>
  );
};

export default ChatMessagesList;
